import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuth } from '../auth/AuthProvider';
import { issueClaudeToken, type ClaudeTokenScope } from '../lib/claude-token';

// #332: AutoRunToggle removed — the desktop scheduler that this stub
// would have controlled is gone. Every cycle is user-triggered.

interface GenerateTokenFormProps {
  intent: string; scope: ClaudeTokenScope; allowAutoMerge: boolean;
  loading: boolean; error: string | null; token: string | null;
  setIntent: (v: string) => void; setScope: (v: ClaudeTokenScope) => void;
  setAllowAutoMerge: (v: boolean) => void;
  onGenerate: () => void;
}

export function GenerateTokenForm({
  intent, scope, allowAutoMerge, loading, error, token,
  setIntent, setScope, setAllowAutoMerge, onGenerate,
}: GenerateTokenFormProps): React.ReactElement {
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-text-primary">Generate Claude Token</p>
      <div>
        <label htmlFor="token-intent" className="block text-xs text-text-secondary mb-1">Intent</label>
        <input id="token-intent" value={intent} onChange={(e) => setIntent(e.target.value)}
          placeholder="e.g. triage session 2026-06-14"
          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-brand-500" />
      </div>
      <div>
        <label htmlFor="token-scope" className="block text-xs text-text-secondary mb-1">Scope</label>
        <select id="token-scope" value={scope}
          onChange={(e) => setScope(e.target.value as ClaudeTokenScope)}
          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-brand-500">
          <option value="read-only">Read-only (introspection)</option>
          <option value="read-and-propose-fix">Read + propose fix (open PRs)</option>
        </select>
      </div>
      {scope === 'read-and-propose-fix' && (
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={allowAutoMerge}
            onChange={(e) => setAllowAutoMerge(e.target.checked)} />
          Allow auto-merge (when CI green + path allowlist + ≤200 LOC)
        </label>
      )}
      <button onClick={onGenerate} disabled={loading || !intent}
        className="w-full px-3 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50">
        {loading ? 'Generating…' : 'Generate'}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {token && (
        <div>
          <p className="text-xs text-text-secondary mb-1">Token (copy now — shown once)</p>
          <code className="block text-xs bg-surface-muted p-2 rounded break-all select-all">{token}</code>
        </div>
      )}
    </div>
  );
}

function GenerateTokenDropdown(): React.ReactElement {
  const [intent, setIntent] = useState('');
  const [scope, setScope] = useState<ClaudeTokenScope>('read-only');
  const [allowAutoMerge, setAllowAutoMerge] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate(): Promise<void> {
    setLoading(true); setError(null);
    try {
      const res = await issueClaudeToken({ intent, scope, allowAutoMerge });
      setToken(res.token);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500">
          + Claude Token
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="z-50 w-80 bg-white border border-border rounded-lg shadow-lg p-4" align="end" sideOffset={8}>
          <GenerateTokenForm
            intent={intent} scope={scope} allowAutoMerge={allowAutoMerge}
            loading={loading} error={error} token={token}
            setIntent={setIntent} setScope={setScope} setAllowAutoMerge={setAllowAutoMerge}
            onGenerate={handleGenerate}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function Header(): React.ReactElement {
  const { actor, signOut } = useAuth();

  return (
    <header className="h-14 shrink-0 bg-white border-b border-border flex items-center justify-between px-6">
      <div />
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          MFA ✓
        </span>
        <GenerateTokenDropdown />
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-secondary">{actor?.actorEmail ?? '—'}</span>
          <button onClick={signOut} className="text-xs text-text-muted hover:text-text-secondary underline">
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
